/**
 * Bunny.net CDN Manager Component
 * Verwaltet Dateien und Ordner in Bunny.net Storage
 */

import React, { useState, useEffect, useCallback, useId } from 'react';
import {
  FolderIcon,
  DocumentIcon,
  TrashIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  MusicalNoteIcon,
  FilmIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { bunnyCDNService, BunnyFile, BunnyStorageStats } from '../../services/bunnyCDNService';
import { getAccessToken } from '../../lib/authToken';
import ErrorBoundary from './ErrorBoundary';
import { Benachrichtigung } from '../ui/Benachrichtigung';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useBenachrichtigung } from '../../hooks/useBenachrichtigung';
import { useFokusfang } from '../../hooks/useFokusfang';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { inBlöcke } from '../../utils/bloecke';

interface BunnyCDNManagerProps {
  className?: string;
}

/**
 * Wie viele Dateinamen die Rückfrage höchstens aufzählt.
 *
 * Gedeckelt, weil „Alle auswählen" hier die ganze Ordnerseite meint — bei achtzig Dateien wäre die
 * Liste länger als der Bildschirm, der Gefahren-Knopf läge außerhalb des Sichtfelds und die
 * Aufzählung hätte genau das Gegenteil von Klarheit erreicht. Fünf Namen plus Restzahl sagen
 * „diese Art von Dateien, so viele" — mehr braucht die Entscheidung nicht.
 */
const NAMEN_IN_DER_FRAGE = 5;

/**
 * Wie viele Pfade in EINEN Sammel-Aufruf gehen.
 *
 * ⚠️ Muss zu `MAX_SAMMEL_LÖSCHUNG` in `controllers/BunnyCDNController.ts` passen — der Endpunkt
 * weist größere Blöcke mit 400 ab. Größer heißt nicht besser: der Aufruf hält so lange offen, wie
 * das Löschen bei Bunny dauert.
 *
 * Warum es die Blöcke überhaupt gibt: „Alle auswählen" meint die ganze Ordnerseite, und die kann
 * mehr als 200 Dateien tragen. Ohne Blöcke bekäme die Nutzerin dort eine Fehlermeldung statt einer
 * Löschung.
 */
const SAMMEL_BLOCKGRÖSSE = 200;

/**
 * Die Auswahl als Aufzählung für den Dialogtext.
 *
 * Nur der Dateiname, nicht der ganze Pfad: alle ausgewählten Dateien liegen im selben, gerade
 * geöffneten Ordner (`loadFiles` lädt genau einen), der Pfad wäre also auf jeder Zeile derselbe
 * Vorspann. `\n` trennt die Zeilen — die Rückfrage stellt Umbrüche über `whitespace-pre-line` dar,
 * anders als `window.confirm()` sie NICHT dargestellt hätte.
 */
interface MediathekDialogProps {
  /** Überschrift. Wird als Name des Dialogs angesagt (`aria-labelledby`). */
  titel: string;
  /**
   * Der erklärende Block über dem Eingabefeld, falls es einen gibt. Wird mit `aria-describedby`
   * am Dialog verknüpft — sonst sagt eine Vorlesehilfe beim Betreten nur „Dialog" und die
   * Warnung, dass hier endgültig gelöscht wird, bliebe ungelesener Fließtext daneben.
   */
  beschreibung?: React.ReactNode;
  /** Escape UND Klick auf den Schleier. Beide Wege sind ausdrücklich derselbe. */
  beiSchließen: () => void;
  children: React.ReactNode;
}

/**
 * Hülle für die drei Dialoge dieser Insel — Ordner anlegen, Ordner löschen, Datei löschen.
 *
 * ⚠️ Warum es diese Hülle gibt und nicht dreimal dasselbe Gerüst: die drei fingen den Fokus nicht.
 * Im gebauten Stand nachgemessen — EIN Shift+Tab aus dem Eingabefeld des offenen Ordner-Dialogs
 * setzte den Fokus auf „Ausgewählte löschen", einen Knopf HINTER dem Schleier. Wer mit der Tastatur
 * arbeitet, stand damit auf einem Bedienelement, das er nicht sieht.
 *
 * ⚠️ Warum NICHT die `Rueckfrage`: die drei sind FORMULAR-Dialoge. Jeder trägt ein Eingabefeld —
 * einmal den neuen Ordnernamen, zweimal den zur Bestätigung abzutippenden Namen. Die `Rueckfrage`
 * kennt nur Titel, Text und zwei Knöpfe; sie könnte keinen von ihnen ersetzen. Geteilt wird
 * deshalb nicht der Dialog, sondern das, woran es fehlte: `useFokusfang` trägt Fang, Escape und
 * Fokus-Rückkehr für BEIDE — hier und in `ui/Rueckfrage.tsx`.
 *
 * ⚠️ Warum die Hülle hier steht und nicht unter `components/ui/`: sie trägt die Klassennamen der
 * CSS-Insel (`mediathek-dialog-schleier`, `mediathek-dialog-karte`, `delete-warning`), und die
 * sind in genau dieser Datei als `<style>` definiert. Ein Baustein unter `ui/`, der Namen aus einer
 * fremden Insel schreibt, wäre eine unsichtbare Kopplung — und der Kollisions-Wächter in
 * `__tests__/BunnyCDNManager.test.tsx` prüft die Insel gegen `index.css`, nicht gegen verstreute
 * Aufrufer. Die Stapelhöhe bleibt unberührt: der Schleier ist derselbe Knoten wie zuvor, also
 * weiterhin `z-index: 50` aus der Insel — unter der `z-[55]` der Rückfrage, unter der `z-[60]` des
 * Balkens.
 *
 * `role="dialog"`, nicht `alertdialog`: die dringende Rolle bleibt der `Rueckfrage` vorbehalten,
 * deren ganzer Inhalt die Meldung IST. Hier ist der Text eine Anweisung an das Eingabefeld
 * („bitte gib den Ordnernamen ein"), und die Antwort wird getippt, nicht gewählt.
 */
const MediathekDialog: React.FC<MediathekDialogProps> = ({
  titel,
  beschreibung,
  beiSchließen,
  children,
}) => {
  const titelId = useId();
  const textId = useId();
  // Ohne `anfangsfokus`: das erste Ziel im Dialog IST das Eingabefeld. Das frühere `autoFocus` am
  // Feld ist damit weg — und musste weg, siehe Begründung an der Eigenschaft im Haken.
  const flächeRef = useFokusfang<HTMLDivElement>({ beiEscape: beiSchließen });

  return (
    <div className="mediathek-dialog-schleier" onClick={beiSchließen}>
      <div
        ref={flächeRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        aria-describedby={beschreibung ? textId : undefined}
        className="mediathek-dialog-karte"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titelId}>{titel}</h3>
        {beschreibung && (
          <div id={textId} className="delete-warning">
            {beschreibung}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

const auswahlAlsListe = (pfade: readonly string[]): string => {
  const namen = pfade.map((pfad) => pfad.split('/').pop() || pfad);
  const gezeigt = namen.slice(0, NAMEN_IN_DER_FRAGE);
  const rest = namen.length - gezeigt.length;
  const zeilen = gezeigt.map((name) => `• ${name}`);
  if (rest > 0) zeilen.push(`• … und ${rest} weitere`);
  return zeilen.join('\n');
};

export const BunnyCDNManager: React.FC<BunnyCDNManagerProps> = ({ className = '' }) => {
  const [files, setFiles] = useState<BunnyFile[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<BunnyStorageStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Zustand und Zeitmessung liegen im Haken. Der frühere Effekt hier blendete BEIDE Tonlagen nach
  // 3000 ms aus — auch „Löschen fehlgeschlagen"; jetzt bleibt der Fehler mit Schließen-Knopf stehen.
  const { inhalt, melde, schließe } = useBenachrichtigung();
  // ⚠️ `rückfrage`, nicht `inhalt` — der Name ist eine Zeile höher schon vergeben. Genau dafür
  // heißt das Feld im Haken so; Begründung in `useRueckfrage.ts`.
  const { rückfrage, frage, antworte } = useRueckfrage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [deleteFileConfirmName, setDeleteFileConfirmName] = useState('');

  // Prüfe Konfiguration beim Laden
  useEffect(() => {
    checkConfiguration();
  }, []);

  const checkConfiguration = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await bunnyCDNService.checkConfiguration();
      
      if (result.success && result.data) {
        setIsConfigured(result.data.configured);
        if (!result.data.configured) {
          setError('Bunny.net CDN ist nicht konfiguriert. Bitte konfigurieren Sie BUNNY_API_KEY und BUNNY_STORAGE_ZONE in der Backend .env-Datei.');
        } else {
          setError(null);
        }
      } else {
        const errorMessage = result.message || 'Fehler beim Prüfen der Konfiguration';
        setError(errorMessage);
        setIsConfigured(false);
        
        // Zusätzliche Hilfe bei verschiedenen Fehlertypen
        if (errorMessage.includes('Authentifizierung erforderlich')) {
          const token = await getAccessToken();
          if (!token) {
            setError(`${errorMessage}\n\nSie sind nicht angemeldet. Klicken Sie auf „Abmelden“ und melden Sie sich danach erneut an, um auf Bunny.net CDN zuzugreifen.`);
          } else {
            setError(`${errorMessage}\n\nIhr Login-Token ist möglicherweise abgelaufen. Bitte melden Sie sich erneut an.\n\nKlicken Sie auf „Abmelden“ und danach auf „Anmelden“, um sich erneut anzumelden.`);
          }
        } else if (errorMessage.includes('Zugriff verweigert')) {
          setError(`${errorMessage}\n\nSie benötigen Admin-Rechte, um auf Bunny.net CDN zuzugreifen.\n\nBitte kontaktieren Sie einen Administrator, um Admin-Rechte zu erhalten.`);
        } else if (errorMessage.includes('Backend-Server nicht erreichbar') || errorMessage.includes('fetch')) {
          setError(`${errorMessage}\n\nBitte stellen Sie sicher, dass:\n1. Der Backend-Server läuft (Port 3000)\n2. Die API_BASE_URL korrekt konfiguriert ist\n3. CORS korrekt eingerichtet ist`);
        }
      }
    } catch (error) {
      console.error('Fehler beim Prüfen der Konfiguration:', error);
      setError(`Unerwarteter Fehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      setIsConfigured(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFiles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    const result = await bunnyCDNService.listFiles(currentFolder);
    
    if (result.success && result.data) {
      setFiles(result.data);
    } else {
      setError(result.message || 'Fehler beim Laden der Dateien');
      setFiles([]);
    }
    
    setIsLoading(false);
  }, [currentFolder]);

  const loadFolders = useCallback(async () => {
    const result = await bunnyCDNService.listFolders(currentFolder);
    
    if (result.success && result.data) {
      setFolders(result.data);
    }
  }, [currentFolder]);

  const loadStats = useCallback(async () => {
    const result = await bunnyCDNService.getStorageStats();
    
    if (result.success && result.data) {
      setStats(result.data);
    }
  }, []);

  // Steht bewusst NACH den memoisierten Ladern: seit sie in den Abhängigkeiten stehen,
  // werden die Konstanten schon beim Rendern gelesen — davor wäre das die temporale Todeszone.
  useEffect(() => {
    if (isConfigured) {
      loadFiles();
      loadFolders();
      loadStats();
    }
  }, [isConfigured, loadFiles, loadFolders, loadStats]);

  const handleDeleteFile = (filePath: string) => {
    setFileToDelete(filePath);
    setDeleteFileConfirmName('');
    setShowDeleteFileConfirm(true);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;

    // Extrahiere den Dateinamen aus dem Pfad (letzter Teil nach dem letzten /)
    const fileName = fileToDelete.split('/').pop() || fileToDelete;

    // Prüfe, ob der eingegebene Name mit dem Dateinamen übereinstimmt
    if (deleteFileConfirmName.trim() !== fileName) {
      melde('error', 'Der eingegebene Dateiname stimmt nicht überein. Bitte geben Sie den exakten Dateinamen ein.');
      return;
    }

    setError(null);
    const result = await bunnyCDNService.deleteFile(fileToDelete);

    if (result.success) {
      melde('success', 'Datei erfolgreich gelöscht');
      setShowDeleteFileConfirm(false);
      setFileToDelete(null);
      setDeleteFileConfirmName('');
      await loadFiles();
      await loadStats();
    } else {
      melde('error', result.message || 'Löschen fehlgeschlagen');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      melde('error', 'Bitte geben Sie einen Ordnernamen ein');
      return;
    }

    const folderPath = currentFolder 
      ? `${currentFolder}/${newFolderName.trim()}`
      : newFolderName.trim();

    setError(null);
    const result = await bunnyCDNService.createFolder(folderPath);

    if (result.success) {
      melde('success', 'Ordner erfolgreich erstellt');
      setShowCreateFolder(false);
      setNewFolderName('');
      await loadFolders();
      await loadStats();
    } else {
      melde('error', result.message || 'Ordner-Erstellung fehlgeschlagen');
    }
  };

  const handleDeleteFolder = (folderPath: string) => {
    setFolderToDelete(folderPath);
    setDeleteConfirmName('');
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;

    // Extrahiere den Ordnernamen aus dem Pfad (letzter Teil nach dem letzten /)
    const folderName = folderToDelete.split('/').pop() || folderToDelete;

    // Prüfe, ob der eingegebene Name mit dem Ordnernamen übereinstimmt
    if (deleteConfirmName.trim() !== folderName) {
      melde('error', 'Der eingegebene Ordnername stimmt nicht überein. Bitte geben Sie den exakten Ordnernamen ein.');
      return;
    }

    setError(null);
    const result = await bunnyCDNService.deleteFolder(folderToDelete);

    if (result.success) {
      melde('success', 'Ordner erfolgreich gelöscht');
      setShowDeleteConfirm(false);
      setFolderToDelete(null);
      setDeleteConfirmName('');
      await loadFolders();
      await loadFiles();
      await loadStats();
    } else {
      melde('error', result.message || 'Ordner-Löschung fehlgeschlagen');
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      melde('success', 'CDN-URL erfolgreich in die Zwischenablage kopiert');
    } catch {
      // Fallback für ältere Browser
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        melde('success', 'CDN-URL erfolgreich in die Zwischenablage kopiert');
      } catch {
        melde('error', 'Fehler beim Kopieren der URL');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleFolderClick = (folder: string) => {
    const newPath = currentFolder ? `${currentFolder}/${folder}` : folder;
    setCurrentFolder(newPath);
    setSelectedFiles(new Set());
  };

  const handleFileSelect = (filePath: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filePath)) {
        newSet.delete(filePath);
      } else {
        newSet.add(filePath);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === files.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(files.map(f => f.path)));
    }
  };

  // ⚠️ Was der Text behauptet, ist am Backend nachgemessen: `BunnyCDNService.deleteFile` schickt
  // ein rohes `DELETE` an `storage.bunnycdn.com` und fasst die Datenbank NICHT an. Ein Cover oder
  // eine Tonspur, die in einem Inhalt verlinkt ist, verliert damit ihre Datei, behält aber ihren
  // Verweis — die Stelle lädt danach ins Leere. Das gehört in die Frage, denn genau das sieht man
  // der Dateiliste nicht an.
  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return;

    // ⚠️ Die Auswahl wird VOR der Frage festgehalten und danach diese Liste gelöscht, nicht der
    // Zustand. Zwischen Frage und Antwort liegt jetzt echte Zeit; würde die Schleife unten wieder
    // `selectedFiles` lesen, könnte sie mehr oder anderes löschen, als der Dialog benannt hat.
    // Genau diese Lücke gab es bei `window.confirm()` nicht, weil das den Faden anhielt.
    const pfade = [...selectedFiles];
    const mehrere = pfade.length !== 1;

    if (
      !(await frage({
        titel: mehrere ? `${pfade.length} Dateien löschen?` : 'Datei löschen?',
        text:
          `${mehrere ? 'Diese Dateien werden' : 'Diese Datei wird'} im Speicher endgültig ` +
          'gelöscht. Verweise darauf in Inhalten oder Titeln bleiben stehen und laufen danach ' +
          'ins Leere.\n\n' +
          auswahlAlsListe(pfade),
        bestätigenText: 'Endgültig löschen',
        gefahr: true,
      }))
    ) {
      return;
    }

    setError(null);

    // EIN Aufruf je Block statt einer je Datei. Bei mehr als `SAMMEL_BLOCKGRÖSSE` Dateien sind es
    // entsprechend mehrere Blöcke — 1000 Dateien werden zu 5 Anfragen, nicht zu 1000.
    const gelungen: string[] = [];
    const gescheitert: string[] = [];
    let abbruch: string | null = null;

    const blöcke = inBlöcke(pfade, SAMMEL_BLOCKGRÖSSE);

    for (const [nr, block] of blöcke.entries()) {
      const result = await bunnyCDNService.deleteFiles(block);

      if (!result.success || !result.data) {
        // ⚠️ Der Block ist als GANZER gescheitert (Netz weg, 429, 500). Was danach kommt, wird gar
        // nicht erst versucht: liefe die Schleife weiter, stünde am Ende eine Meldung, die den
        // Grund des Abbruchs verschweigt. Die restlichen Pfade zählen als „nicht gelöscht" — was
        // sie sind.
        abbruch = result.message || 'Löschen fehlgeschlagen';
        gescheitert.push(...blöcke.slice(nr).flat());
        break;
      }

      for (const eintrag of result.data.results) {
        (eintrag.deleted ? gelungen : gescheitert).push(eintrag.path);
      }
    }

    // ⚠️ Nur die gelöschten Dateien verlassen die Auswahl. Vorher wurde sie pauschal geleert — nach
    // einer Teil-Löschung stand die Nutzerin damit ohne die Liste da, die sie zum erneuten Versuch
    // gebraucht hätte, und ohne zu wissen, welche Dateien überhaupt noch existieren.
    setSelectedFiles(new Set(gescheitert));

    if (gescheitert.length === 0) {
      melde('success', `${gelungen.length} ${gelungen.length === 1 ? 'Datei' : 'Dateien'} gelöscht.`);
    } else {
      // ⚠️ Tonlage „error", sobald etwas offen blieb — auch wenn die Mehrzahl geklappt hat. Eine
      // Erfolgsmeldung blendet sich nach drei Sekunden aus; genau diese Meldung muss stehen
      // bleiben. Und sie nennt die Dateien beim Namen: „100 gelöscht, 100 Fehler" sagt der
      // Nutzerin nicht, welche Datei noch da ist.
      const kopf =
        gelungen.length > 0
          ? `${gelungen.length} von ${gelungen.length + gescheitert.length} Dateien gelöscht. Nicht gelöscht und weiterhin ausgewählt:`
          : 'Keine Datei wurde gelöscht. Weiterhin ausgewählt:';
      melde('error', `${kopf}\n${auswahlAlsListe(gescheitert)}${abbruch ? `\n\nGrund: ${abbruch}` : ''}`);
    }

    if (gelungen.length > 0) {
      // Genau EINMAL am Ende, nicht je Datei.
      await loadFiles();
      await loadStats();
    }
  };

  // Filter Dateien basierend auf Suchanfrage
  const filteredFiles = files.filter(file => 
    file.path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('audio/')) return <MusicalNoteIcon className="w-6 h-6 text-blue-500" />;
    if (contentType.startsWith('video/')) return <FilmIcon className="w-6 h-6 text-purple-500" />;
    if (contentType.startsWith('image/')) return <PhotoIcon className="w-6 h-6 text-green-500" />;
    if (contentType === 'application/pdf') return <DocumentTextIcon className="w-6 h-6 text-red-500" />;
    return <DocumentIcon className="w-6 h-6 text-gray-500" />;
  };

  if (!isConfigured && !isLoading) {
    // Formatiere Fehlermeldung für mehrzeilige Anzeige
    const formatErrorMessage = (message: string | null) => {
      if (!message) return 'Bitte konfigurieren Sie BUNNY_API_KEY und BUNNY_STORAGE_ZONE in der Backend .env-Datei.';
      
      // Ersetze \n durch <br> für HTML-Anzeige
      return message.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < message.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    };

    return (
      <div className={`bunny-cdn-manager ${className}`}>
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Bunny.net CDN nicht konfiguriert</h2>
          <div className="error-message" style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
            {formatErrorMessage(error)}
          </div>
          <div className="error-help" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem' }}>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 'bold' }}>Hilfe:</h3>
            <ol style={{ marginLeft: '1.5rem', fontSize: '0.875rem' }}>
              <li>Stellen Sie sicher, dass der Backend-Server läuft (Port 3000)</li>
              <li>Prüfen Sie die API_BASE_URL in der Frontend-Konfiguration</li>
              <li>Konfigurieren Sie BUNNY_API_KEY und BUNNY_STORAGE_ZONE in der Backend .env-Datei</li>
              <li>Prüfen Sie die Browser-Konsole für weitere Details</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={`bunny-cdn-manager ${className}`}>
        {/* Header */}
        <div className="manager-header">
          <div className="header-content">
            <h1 className="manager-title">Bunny.net CDN Manager</h1>
            <p className="manager-subtitle">Dateien und Ordner in Bunny.net Storage verwalten</p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="stats-bar">
              <div className="stat-item">
                <span className="mediathek-kennzahl-name">Dateien:</span>
                <span className="mediathek-kennzahl-wert">{stats.totalFiles}</span>
              </div>
              <div className="stat-item">
                <span className="mediathek-kennzahl-name">Größe:</span>
                <span className="mediathek-kennzahl-wert">{bunnyCDNService.formatFileSize(stats.totalSize)}</span>
              </div>
              <div className="stat-item">
                <span className="mediathek-kennzahl-name">Ordner:</span>
                <span className="mediathek-kennzahl-wert">{stats.folders}</span>
              </div>
            </div>
          )}
        </div>

        {/* ⚠️ SICHTBARE ÄNDERUNG: hier lag als einziger der zwölf Orte keine Tailwind-Kette, sondern
            eine eigene CSS-Insel (`.notification` mit `padding: 12px 16px`, `border-radius: 4px`,
            `font-size: 14px` und den Bootstrap-Hexwerten `#d4edda`/`#f8d7da`). Der Balken trägt
            jetzt die Mehrheitsfassung `p-4 rounded-lg` mit den `success`/`error`-Tokens; die drei
            Insel-Regeln sind unten mit entfernt.

            ⚠️ `schwebend` ist hier PFLICHT, nicht Geschmack — derselbe Fall wie auf den
            Themen-Kategorien, nur eine Etage tiefer. Drei Fehlerpfade melden bei OFFENEM Dialog:
            leerer Ordnername (`handleCreateFolder`), nicht übereinstimmender Dateiname
            (`confirmDeleteFile`) und nicht übereinstimmender Ordnername (`confirmDeleteFolder`).
            Alle drei kehren mit `return` zurück, ohne den Dialog zu schließen — bewusst, damit die
            Eingabe nicht verloren geht. Im Seitenfluss rendert der Balken dann hinter dem
            abgedunkelten Schleier: die Nutzerin klickt „Erstellen" mit leerem Feld, sichtbar
            passiert nichts, es sieht aus, als sei der Klick ins Leere gegangen.

            Damit entfällt auch der frühere `mb-4`-Rahmen samt `inhalt`-Abfrage: der schwebende
            Balken steht `position: fixed` und braucht keinen Abstand im Fluss. Die Abfrage war
            sogar schädlich — sie montierte die beiden `sr-only`-Ansage-Ebenen des Bausteins erst
            MIT ihrem Text, also genau der Fall, den eine Live-Region nicht ansagt. Ohne sie stehen
            sie ab dem ersten Rendern da (`position: absolute`, keine Layout-Wirkung). */}
        <Benachrichtigung inhalt={inhalt} onSchließen={schließe} schwebend />

        {/* Die Rückfrage vor dem Löschen der Auswahl.
            ⚠️ Der Grund, warum sie hier NICHT auf `z-50` liegen darf, steht direkt darunter in der
            Insel: `.mediathek-dialog-schleier` trägt selbst `z-index: 50`. Beide hängen am selben
            `document.body` (die Rückfrage per Portal aus `ModalSchicht`), bei Gleichstand
            entschiede also die Dokumentreihenfolge. Die `z-[55]` des Bausteins schlägt die 50
            erklärt — nachgemessen im gebauten Stand mit einer Trefferprobe, nicht angenommen; die
            Werte und Kontrollpunkte stehen in `__tests__/BunnyCDNManager.test.tsx`.
            ⚠️ Der Weg, auf dem die Überlagerung gemessen wurde, ist inzwischen zu: seit die
            Insel-Dialoge über `MediathekDialog` den Fokus fangen, führt aus dem offenen
            Ordner-Dialog weder Tabulator noch Maus auf „Ausgewählte löschen". Die Ordnung bleibt
            trotzdem stehen — sie ist die Zusage, nicht die Folge eines Fangs, den der nächste
            Umbau wieder verlieren könnte. Beide Schichten können weiter gleichzeitig im Dokument
            stehen (der Test unten baut genau das auf); welche von beiden dann die Tastatur
            besitzt, entscheidet `useFokusfang` am zuletzt geöffneten Dialog. */}
        <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

        {/* Error */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Toolbar */}
        <div className="toolbar">
          <div className="toolbar-left">
            {/* Breadcrumb */}
            <div className="breadcrumb">
              <button
                className="breadcrumb-item"
                onClick={() => setCurrentFolder('')}
                disabled={!currentFolder}
              >
                Root
              </button>
              {currentFolder.split('/').filter(Boolean).map((folder, index, arr) => {
                const path = arr.slice(0, index + 1).join('/');
                return (
                  <React.Fragment key={path}>
                    <span className="breadcrumb-separator">/</span>
                    <button
                      className="breadcrumb-item"
                      onClick={() => setCurrentFolder(path)}
                    >
                      {folder}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="toolbar-right">
            {/* Search */}
            <div className="search-box">
              <MagnifyingGlassIcon className="search-icon" />
              <input
                type="text"
                placeholder="Dateien durchsuchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Actions */}
            <div className="action-buttons">
              {/* ⚠️ Hier stand „Datei hochladen" (Label mit verstecktem Datei-Feld). Entfallen am
                  2026-09-08, Tom-Entscheid: die CMS-Aufrufer des Uploads gehen, der Ingest
                  befuellt den Speicher. Die Mediathek bleibt vollstaendig BEDIENBAR — durchsuchen,
                  Ordner anlegen, auswaehlen, loeschen; nur das Hinzufuegen von aussen faellt weg.
                  ⛔ `POST /api/admin/bunny/upload` selbst steht noch: die Route abzuraeumen ist
                  🌐s Zug, und die Reihenfolge ist Absicht (erst Aufrufer, dann Route). */}

              <button
                className="mediathek-knopf mediathek-knopf-zweit"
                onClick={() => setShowCreateFolder(true)}
              >
                <PlusIcon className="btn-icon" />
                Ordner erstellen
              </button>

              {selectedFiles.size > 0 && (
                <button
                  className="mediathek-knopf btn-danger"
                  onClick={handleDeleteSelected}
                >
                  <TrashIcon className="btn-icon" />
                  Ausgewählte löschen ({selectedFiles.size})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Create Folder Modal */}
        {showCreateFolder && (
          <MediathekDialog titel="Neuen Ordner erstellen" beiSchließen={() => setShowCreateFolder(false)}>
            <input
              type="text"
              placeholder="Ordnername"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="modal-input"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <div className="modal-actions">
              <button className="mediathek-knopf mediathek-knopf-haupt" onClick={handleCreateFolder}>
                Erstellen
              </button>
              <button className="mediathek-knopf mediathek-knopf-zweit" onClick={() => {
                setShowCreateFolder(false);
                setNewFolderName('');
              }}>
                Abbrechen
              </button>
            </div>
          </MediathekDialog>
        )}

        {/* Delete Folder Confirmation Modal */}
        {showDeleteConfirm && folderToDelete && (
          <MediathekDialog
            titel="Ordner löschen bestätigen"
            beiSchließen={() => {
              setShowDeleteConfirm(false);
              setFolderToDelete(null);
              setDeleteConfirmName('');
            }}
            beschreibung={
              <>
                <p style={{ marginBottom: '12px', color: '#dc3545', fontWeight: '600' }}>
                  ⚠️ Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Der Ordner <strong>"{folderToDelete}"</strong> und alle darin enthaltenen Dateien werden permanent gelöscht.
                </p>
                <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                  Bitte geben Sie den Ordnernamen <strong>"{folderToDelete.split('/').pop() || folderToDelete}"</strong> ein, um zu bestätigen:
                </p>
              </>
            }
          >
            <input
              type="text"
              placeholder="Ordnername eingeben"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              className="modal-input"
              onKeyPress={(e) => e.key === 'Enter' && confirmDeleteFolder()}
              style={{ borderColor: deleteConfirmName && deleteConfirmName.trim() !== (folderToDelete.split('/').pop() || folderToDelete) ? '#dc3545' : '#ddd' }}
            />
            {deleteConfirmName && deleteConfirmName.trim() !== (folderToDelete.split('/').pop() || folderToDelete) && (
              <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '-12px', marginBottom: '12px' }}>
                Der eingegebene Name stimmt nicht überein
              </p>
            )}
            <div className="modal-actions">
              <button
                className="mediathek-knopf btn-danger"
                onClick={confirmDeleteFolder}
                disabled={deleteConfirmName.trim() !== (folderToDelete.split('/').pop() || folderToDelete)}
                style={{ opacity: deleteConfirmName.trim() !== (folderToDelete.split('/').pop() || folderToDelete) ? 0.5 : 1, cursor: deleteConfirmName.trim() !== (folderToDelete.split('/').pop() || folderToDelete) ? 'not-allowed' : 'pointer' }}
              >
                <TrashIcon className="btn-icon" />
                Endgültig löschen
              </button>
              <button className="mediathek-knopf mediathek-knopf-zweit" onClick={() => {
                setShowDeleteConfirm(false);
                setFolderToDelete(null);
                setDeleteConfirmName('');
              }}>
                Abbrechen
              </button>
            </div>
          </MediathekDialog>
        )}

        {/* Delete File Confirmation Modal */}
        {showDeleteFileConfirm && fileToDelete && (
          <MediathekDialog
            titel="Datei löschen bestätigen"
            beiSchließen={() => {
              setShowDeleteFileConfirm(false);
              setFileToDelete(null);
              setDeleteFileConfirmName('');
            }}
            beschreibung={
              <>
                <p style={{ marginBottom: '12px', color: '#dc3545', fontWeight: '600' }}>
                  ⚠️ Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Die Datei <strong>"{fileToDelete}"</strong> wird permanent gelöscht.
                </p>
                <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                  Bitte geben Sie den Dateinamen <strong>"{fileToDelete.split('/').pop() || fileToDelete}"</strong> ein, um zu bestätigen:
                </p>
              </>
            }
          >
            <input
              type="text"
              placeholder="Dateiname eingeben"
              value={deleteFileConfirmName}
              onChange={(e) => setDeleteFileConfirmName(e.target.value)}
              className="modal-input"
              onKeyPress={(e) => e.key === 'Enter' && confirmDeleteFile()}
              style={{ borderColor: deleteFileConfirmName && deleteFileConfirmName.trim() !== (fileToDelete.split('/').pop() || fileToDelete) ? '#dc3545' : '#ddd' }}
            />
            {deleteFileConfirmName && deleteFileConfirmName.trim() !== (fileToDelete.split('/').pop() || fileToDelete) && (
              <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '-12px', marginBottom: '12px' }}>
                Der eingegebene Name stimmt nicht überein
              </p>
            )}
            <div className="modal-actions">
              <button
                className="mediathek-knopf btn-danger"
                onClick={confirmDeleteFile}
                disabled={deleteFileConfirmName.trim() !== (fileToDelete.split('/').pop() || fileToDelete)}
                style={{ opacity: deleteFileConfirmName.trim() !== (fileToDelete.split('/').pop() || fileToDelete) ? 0.5 : 1, cursor: deleteFileConfirmName.trim() !== (fileToDelete.split('/').pop() || fileToDelete) ? 'not-allowed' : 'pointer' }}
              >
                <TrashIcon className="btn-icon" />
                Endgültig löschen
              </button>
              <button className="mediathek-knopf mediathek-knopf-zweit" onClick={() => {
                setShowDeleteFileConfirm(false);
                setFileToDelete(null);
                setDeleteFileConfirmName('');
              }}>
                Abbrechen
              </button>
            </div>
          </MediathekDialog>
        )}

        {/* Content */}
        <div className="manager-content">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Lade Dateien...</p>
            </div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="folders-section">
                  <h3 className="section-title">Ordner</h3>
                  <div className="items-grid">
                    {folders.map((folder) => (
                      <div
                        key={folder}
                        className="folder-item"
                        onClick={() => handleFolderClick(folder)}
                      >
                        <FolderIcon className="folder-icon" />
                        <span className="item-name">{folder}</span>
                        <button
                          className="item-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(currentFolder ? `${currentFolder}/${folder}` : folder);
                          }}
                          title="Ordner löschen"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              <div className="files-section">
                <div className="section-header">
                  <h3 className="section-title">Dateien</h3>
                  {files.length > 0 && (
                    <label className="select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedFiles.size === files.length && files.length > 0}
                        onChange={handleSelectAll}
                      />
                      Alle auswählen
                    </label>
                  )}
                </div>

                {filteredFiles.length === 0 ? (
                  <div className="empty-state">
                    <p>{searchQuery ? 'Keine Dateien gefunden' : 'Keine Dateien in diesem Ordner'}</p>
                  </div>
                ) : (
                  <div className="files-table">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <th>Name</th>
                          <th>Typ</th>
                          <th>Größe</th>
                          <th>URL</th>
                          <th style={{ width: '100px' }}>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFiles.map((file) => (
                          <tr
                            key={file.path}
                            className={selectedFiles.has(file.path) ? 'selected' : ''}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedFiles.has(file.path)}
                                onChange={() => handleFileSelect(file.path)}
                              />
                            </td>
                            <td>
                              <div className="file-info">
                                {getFileIcon(file.contentType)}
                                <span className="file-name">{file.path.split('/').pop()}</span>
                              </div>
                            </td>
                            <td>
                              <span className="file-type">{file.contentType}</span>
                            </td>
                            <td>
                              <span className="file-size">{bunnyCDNService.formatFileSize(file.size)}</span>
                            </td>
                            <td>
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-url"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {file.url.substring(0, 50)}...
                              </a>
                            </td>
                            <td>
                              <div className="file-actions">
                                <button
                                  className="action-btn copy-btn"
                                  onClick={() => handleCopyUrl(file.url)}
                                  title="CDN-URL kopieren"
                                >
                                  <ClipboardDocumentIcon className="w-4 h-4" />
                                </button>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleDeleteFile(file.path)}
                                  title="Löschen"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <style>{`
          /* ⚠️ INSELSEITIGE NAMEN — nicht zurückbenennen.
             Dieser Block ist GLOBAL, solange die Mediathek gemountet ist, und er steht im Dokument
             NACH dem gebauten Stylesheet. Jeder Name, den es auch in index.css gibt, wird dort
             also überschrieben. Sieben waren betroffen und tragen deshalb jetzt den Präfix
             "mediathek-":
               .btn .btn-primary .btn-secondary .modal-overlay .modal-content .stat-label .stat-value
             Gemessen am gebauten Stylesheet: ein .btn.btn-primary rendert unter dieser Insel
             rgb(0, 123, 255) statt des Marken-Farbverlaufs, 4px Radius statt 6px, keinen Rahmen.
             .btn steckt in 25 Dateien außerhalb der Mediathek, .btn-primary in 20.
             Sichtbar wurde es am Schließen-Knopf des geteilten Balkens (Benachrichtigung,
             "btn btn-outline"), der auf DIESER Seite mit im Baum steht: er erbte border: none und
             die 4px aus der Insel statt seiner eigenen Gestalt.
             Scoping (".bunny-cdn-manager .btn") wäre KEINE Lösung gewesen: der Balken steht
             innerhalb dieser Wurzel, die Regel hätte ihn mit erhöhter Spezifität weiter getroffen.
             Nur das Umbenennen nimmt ihn heraus.
             .btn-danger, .btn-icon, .action-btn, .modal-input, .modal-actions und .stat-item
             behalten ihre Namen: die gibt es global nicht, sie beschatten also nichts. */
          .bunny-cdn-manager {
            width: 100%;
            padding: 24px;
            background: #f5f5f5;
            min-height: 100vh;
          }

          .manager-header {
            background: white;
            border-radius: 8px;
            padding: 24px;
            margin-bottom: 24px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .header-content {
            margin-bottom: 16px;
          }

          .manager-title {
            font-size: 24px;
            font-weight: 700;
            color: #1a1a1a;
            margin: 0 0 8px 0;
          }

          .manager-subtitle {
            font-size: 14px;
            color: #666;
            margin: 0;
          }

          .stats-bar {
            display: flex;
            gap: 24px;
            padding-top: 16px;
            border-top: 1px solid #e5e5e5;
          }

          .stat-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .mediathek-kennzahl-name {
            font-size: 12px;
            color: #666;
          }

          .mediathek-kennzahl-wert {
            font-size: 18px;
            font-weight: 600;
            color: #1a1a1a;
          }

          /* Hier standen .notification, .notification.success und .notification.error. Mit dem
             Umstieg auf den geteilten Balken sind sie verwaist — deshalb gelöscht.
             .error-message bleibt: sie trägt weiterhin den Lade- und Konfigurationsfehler, oben
             im Regelfall und im Nicht-konfiguriert-Zustand.
             ⚠️ KEINE Backticks in diesem Block: der Inhalt ist ein Template-String, ein Backtick
             beendet ihn und macht aus der nächsten Zeile einen getaggten Aufruf. */
          .error-message {
            padding: 12px 16px;
            background: #f8d7da;
            color: #721c24;
            border-radius: 4px;
            margin-bottom: 16px;
          }

          .error-state {
            text-align: center;
            padding: 80px 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .error-icon {
            font-size: 48px;
            margin-bottom: 16px;
          }

          .toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: white;
            padding: 16px 24px;
            border-radius: 8px;
            margin-bottom: 24px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            flex-wrap: wrap;
            gap: 16px;
          }

          .toolbar-left,
          .toolbar-right {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .breadcrumb {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .breadcrumb-item {
            background: none;
            border: none;
            color: #666;
            cursor: pointer;
            font-size: 14px;
            padding: 4px 8px;
            border-radius: 4px;
            transition: background 0.2s;
          }

          .breadcrumb-item:hover:not(:disabled) {
            background: #f0f0f0;
          }

          .breadcrumb-item:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .breadcrumb-separator {
            color: #999;
          }

          .search-box {
            position: relative;
            display: flex;
            align-items: center;
          }

          .search-icon {
            position: absolute;
            left: 12px;
            width: 18px;
            height: 18px;
            color: #999;
          }

          .search-input {
            padding: 8px 12px 8px 36px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            width: 250px;
          }

          .action-buttons {
            display: flex;
            gap: 8px;
          }

          .mediathek-knopf {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .btn-icon {
            width: 18px;
            height: 18px;
          }

          .mediathek-knopf-haupt {
            background: #007bff;
            color: white;
          }

          .mediathek-knopf-haupt:hover {
            background: #0056b3;
          }

          .mediathek-knopf-zweit {
            background: #6c757d;
            color: white;
          }

          .mediathek-knopf-zweit:hover {
            background: #545b62;
          }

          .btn-danger {
            background: #dc3545;
            color: white;
          }

          .btn-danger:hover {
            background: #c82333;
          }

          .manager-content {
            background: white;
            border-radius: 8px;
            padding: 24px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .loading-state {
            text-align: center;
            padding: 80px 20px;
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 16px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .folders-section,
          .files-section {
            margin-bottom: 32px;
          }

          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
          }

          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #1a1a1a;
            margin: 0;
          }

          .select-all-checkbox {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            cursor: pointer;
          }

          .items-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 16px;
            margin-bottom: 24px;
          }

          .folder-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            border: 1px solid #e5e5e5;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s;
            position: relative;
          }

          .folder-item:hover {
            background: #f8f9fa;
            border-color: #007bff;
          }

          .folder-icon {
            width: 24px;
            height: 24px;
            color: #ffa500;
          }

          .item-name {
            flex: 1;
            font-size: 14px;
            color: #1a1a1a;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .item-delete {
            background: none;
            border: none;
            color: #dc3545;
            cursor: pointer;
            padding: 4px;
            opacity: 0;
            transition: opacity 0.2s;
          }

          .folder-item:hover .item-delete {
            opacity: 1;
          }

          .files-table {
            overflow-x: auto;
          }

          .files-table table {
            width: 100%;
            border-collapse: collapse;
          }

          .files-table thead {
            background: #f8f9fa;
          }

          .files-table th {
            padding: 12px;
            text-align: left;
            font-size: 12px;
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
            border-bottom: 2px solid #e5e5e5;
          }

          .files-table td {
            padding: 12px;
            border-bottom: 1px solid #e5e5e5;
          }

          .files-table tbody tr {
            transition: background 0.2s;
          }

          .files-table tbody tr:hover {
            background: #f8f9fa;
          }

          .files-table tbody tr.selected {
            background: #e7f3ff;
          }

          .file-info {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .file-name {
            font-size: 14px;
            color: #1a1a1a;
          }

          .file-type {
            font-size: 12px;
            color: #666;
            padding: 2px 8px;
            background: #f0f0f0;
            border-radius: 4px;
          }

          .file-size {
            font-size: 14px;
            color: #666;
          }

          .file-url {
            font-size: 12px;
            color: #007bff;
            text-decoration: none;
            max-width: 200px;
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .file-url:hover {
            text-decoration: underline;
          }

          .file-actions {
            display: flex;
            gap: 8px;
          }

          .action-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background 0.2s;
          }

          .copy-btn {
            color: #007bff;
          }

          .copy-btn:hover {
            background: #e7f3ff;
          }

          .delete-btn {
            color: #dc3545;
          }

          .delete-btn:hover {
            background: #fee;
          }

          .empty-state {
            text-align: center;
            padding: 80px 20px;
            color: #666;
          }

          /* ⚠️ z-index: 50 — GEÄNDERT von 1000, und die Änderung ist der eigentliche Punkt.
             ⚠️ KEINE Backticks hier drin, siehe die Warnung am .error-message-Block weiter oben:
             dieser Kommentar steht in einem Template-String.
             Die 1000 war eine willkürliche Zahl aus der Bootstrap-Konvention dieser Insel. Die
             Anwendung fährt sonst durchgehend Tailwinds Stufen: .modal-overlay (index.css),
             .bunny-file-picker-overlay, ProductPicker, TrackPicker, die Modals der
             Themen-Kategorien und die Sidebar liegen alle auf z-50; der schwebende
             Benachrichtigungs-Balken liegt auf z-[60], damit er ÜBER Overlays steht.
             Den Balken stattdessen auf 1001 zu heben wäre ein Zahlen-Wettrüsten gewesen: die
             nächste Insel bringt 9999 mit, und der Balken müsste wieder nachziehen. Gemessen: die
             1000 hier war der EINZIGE rohe z-index in src/ — alles andere sind Tailwind-Stufen.
             Die Deckung leidet nicht. .bunny-cdn-manager setzt weder position noch transform noch
             opacity, und auf dem Weg zur Wurzel tut das auch niemand sonst (main.main-content =
             "ml-64 flex-1 min-h-screen", .page-content = "p-6") — der Schleier liegt also im
             Wurzel-Stapelkontext. Gegen die Sidebar (ebenfalls z-50, aber Geschwister VOR dem
             main-Element im Baum) entscheidet bei Gleichstand die Dokumentreihenfolge, und die
             spricht für den Schleier. Genau so deckt .modal-overlay die Sidebar seit jeher. */
          .mediathek-dialog-schleier {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 50;
          }

          .mediathek-dialog-karte {
            background: white;
            border-radius: 8px;
            padding: 24px;
            min-width: 400px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          .mediathek-dialog-karte h3 {
            margin: 0 0 16px 0;
            font-size: 18px;
            font-weight: 600;
          }

          .modal-input {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            margin-bottom: 16px;
          }

          .modal-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
          }
        `}</style>
      </div>
    </ErrorBoundary>
  );
};

